import type { AuthProvider } from "@refinedev/core";
import { getCsrfToken } from "./utils";

export const TOKEN_KEY = "refine-auth";
export const USER_KEY = "refine-user";

const ALLAUTH_BASE = "/_allauth/browser/v1";

type AllauthResponse = {
  status: number;
  data?: Record<string, any>;
  meta?: Record<string, any>;
  errors?: Array<{ message?: string; param?: string; code?: string }>;
};

const AUTH_SESSION_PATH = "/auth/session";

function buildHeaders(method: string, withJson = true): HeadersInit {
  const headers: HeadersInit = {
    Accept: "application/json",
  };
  if (withJson) {
    headers["Content-Type"] = "application/json";
  }
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers["X-CSRFToken"] = csrf;
    }
  }
  return headers;
}

async function allauthRequest(
  path: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  body?: Record<string, any>,
): Promise<AllauthResponse> {
  const response = await fetch(`${ALLAUTH_BASE}${path}`, {
    method,
    credentials: "include",
    headers: buildHeaders(method, body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let payload: AllauthResponse;
  try {
    payload = (await response.json()) as AllauthResponse;
  } catch {
    payload = { status: response.status };
  }

  if (typeof payload.status !== "number") {
    payload.status = response.status;
  }

  return payload;
}

function syncLocalUserFromSession(payload: AllauthResponse): void {
  const user = payload.data?.user;
  if (!user) {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    return;
  }
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_KEY, "authenticated");
}

function clearLocalAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function firstErrorMessage(payload: AllauthResponse, fallback: string): string {
  const msg = payload.errors?.[0]?.message;
  return msg || fallback;
}

function isAuthenticatedPayload(payload: AllauthResponse): boolean {
  return (
    payload.status === 200 ||
    (payload.status === 401 && Boolean(payload.meta?.is_authenticated)) ||
    Boolean(payload.meta?.is_authenticated)
  );
}

export const authProvider: AuthProvider = {
  login: async ({ username, email, password }) => {
    try {
      const loginEmail = email || username;
      const payload = await allauthRequest("/auth/login", "POST", {
        email: loginEmail,
        password,
      });

      if (isAuthenticatedPayload(payload)) {
        syncLocalUserFromSession(payload);
        return {
          success: true,
          redirectTo: "/",
        };
      }

      // allauth may return 409 for flow state conflicts while still ending up authenticated.
      const sessionPayload = await allauthRequest(AUTH_SESSION_PATH, "GET");
      if (isAuthenticatedPayload(sessionPayload)) {
        syncLocalUserFromSession(sessionPayload);
        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
        error: {
          name: "LoginError",
          message: firstErrorMessage(payload, "Invalid email or password"),
        },
      };
    } catch {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Network error occurred",
        },
      };
    }
  },

  register: async ({ email, username, password }) => {
    try {
      const signupEmail = email || username;
      const payload = await allauthRequest("/auth/signup", "POST", {
        email: signupEmail,
        password,
      });

      if (payload.status === 200) {
        syncLocalUserFromSession(payload);
        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
        error: {
          name: "RegisterError",
          message: firstErrorMessage(payload, "Signup failed"),
        },
      };
    } catch {
      return {
        success: false,
        error: {
          name: "RegisterError",
          message: "Network error occurred",
        },
      };
    }
  },

  logout: async () => {
    try {
      await allauthRequest(AUTH_SESSION_PATH, "DELETE");
    } catch {
      // Local cleanup still proceeds.
    }
    clearLocalAuth();

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    try {
      const payload = await allauthRequest(AUTH_SESSION_PATH, "GET");
      const authenticated = isAuthenticatedPayload(payload);

      if (authenticated) {
        syncLocalUserFromSession(payload);
        return { authenticated: true };
      }

      clearLocalAuth();
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    } catch {
      clearLocalAuth();
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }
  },

  forgotPassword: async ({ email }) => {
    try {
      const payload = await allauthRequest("/auth/password/request", "POST", { email });
      if (payload.status === 200 || payload.status === 401) {
        return { success: true };
      }
      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: firstErrorMessage(payload, "Could not submit password reset request"),
        },
      };
    } catch {
      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: "Network error occurred",
        },
      };
    }
  },

  getPermissions: async () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return {
        role: "anonymous",
        isStaff: false,
        isSuperuser: false,
      };
    }

    try {
      const userData = JSON.parse(raw);
      const isStaff = Boolean(userData?.is_staff);
      const isSuperuser = Boolean(userData?.is_superuser);
      return {
        role: isSuperuser ? "superuser" : isStaff ? "staff" : "user",
        isStaff,
        isSuperuser,
      };
    } catch {
      return {
        role: "anonymous",
        isStaff: false,
        isSuperuser: false,
      };
    }
  },

  getIdentity: async () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }

    try {
      const userData = JSON.parse(raw);
      const email = userData.email || "";
      let gravatarUrl = "https://www.gravatar.com/avatar/?s=300&d=identicon";

      if (email && typeof crypto !== "undefined" && crypto.subtle) {
        try {
          const digest = await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(email.toLowerCase().trim()),
          );
          const hashHex = Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
          gravatarUrl = `https://www.gravatar.com/avatar/${hashHex}?s=300&d=identicon`;
        } catch {
          // Ignore hashing failures and keep default avatar.
        }
      }

      return {
        id: userData.id,
        name: userData.display || userData.username || userData.email,
        email: userData.email,
        avatar: gravatarUrl,
        isStaff: userData.is_staff,
        isSuperuser: userData.is_superuser,
      };
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    return { error };
  },
};
